import { Avatar, Table, Tooltip } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import Tasks from "entities/Task";
import { get, upperCase } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoFlashOutline } from "react-icons/io5";
import {
  MdDeleteOutline,
  MdNotifications,
  MdNotificationsOff,
  MdOpenInNew,
  MdOutlineCalendarToday,
  MdOutlineCheckCircle,
  MdOutlineCircle,
  MdOutlineFilterTiltShift,
  MdOutlineMail,
  MdOutlinePhone,
  MdOutlineRule,
  MdOutlineSearch,
  MdSchedule,
} from "react-icons/md";
import Button from "shared/components/Button";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Pagination from "shared/components/Pagination";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  TASK_PRIORITIES,
  TASK_STATUSES,
} from "utils/constants";
import { displayDate } from "utils/helpers/date";
import {
  buildTaskTitleSearchFilter,
  resolveTaskEmptyMessage,
} from "./taskSearch";

export const TASK_STATUS_ICON = {
  [TASK_STATUSES.COMPLETED]: <MdOutlineCheckCircle />,
  [TASK_STATUSES.IN_PROGRESS]: <MdOutlineFilterTiltShift />,
};

const List = ({
  filter,
  pagination,
  setPagination,
  onSearch,
  onDelete,
  onOpenDetail,
  onDismissNotification,
  onCancelReminder,
  onReactivateReminder,
  unreadReminderTaskIds = new Set(),
  onDateSearch,
  totalCount,
  automaticCount,
  toDoCount,
  completedCount,
  notAssignedCount,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.TASK);
  const LIST = t("LIST", { returnObjects: true });
  const [activeTab, setActiveTab] = useState("all");
  const [isRangeOpen, setIsRangeOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const onOpenRange = () => setIsRangeOpen(true);
  const onRangeOpenChange = (open) => setIsRangeOpen(open);

  const stats = [
    {
      value: totalCount,
      label: LIST.STATS.TOTAL_TASKS,
      icon: MdOutlineRule,
      color: "blue",
    },
    {
      value: automaticCount,
      label: LIST.STATS.AUTO_CREATED_TASKS,
      icon: IoFlashOutline,
      color: "orange",
    },
    {
      value: toDoCount,
      label: LIST.STATS.REMAINING_TASKS,
      icon: MdOutlineFilterTiltShift,
      color: "gray",
    },
    {
      value: completedCount,
      label: LIST.STATS.COMPLETED_TASKS,
      icon: MdOutlineCheckCircle,
      color: "green",
    },
    {
      value: notAssignedCount,
      label: LIST.STATS.UNASSIGNED_TASKS,
      icon: MdOutlineCircle,
      color: "",
    },
  ];

  const columns = useMemo(
    () => [
      {
        title: LIST.TASK,
        dataIndex: "campaignStep",
        //   sorter: true,
        render: (record, row) => {
          return (
            <div className="task-list__task-cell">
              <div className="task-list__task-name">
                {get(row, "name") || get(record, "name")}
              </div>
              <div className="task-list__task-date">
                {displayDate({ date: get(row, "createdDate") })}
              </div>
            </div>
          );
        },
      },
      {
        title: LIST.TYPE,
        dataIndex: "subtype",
        key: "subtype",
        render: (record) =>
          record ? get(LIST, `SUBTYPES.${record}`, record) : "-",
      },
      {
        title: LIST.PRIORITY,
        dataIndex: "data",
        key: "priority",
        render: (_data, row) => {
          const priority = get(row, "data.priority") || TASK_PRIORITIES.NONE;
          const label = get(LIST, `PRIORITIES.${priority}`, priority);
          return (
            <div
              className={classNames(
                "task-list__priority",
                `task-list__priority--${String(priority).toLowerCase()}`,
              )}
            >
              {label}
            </div>
          );
        },
      },
      {
        title: LIST.DUE,
        dataIndex: "event",
        render: (record) =>
          get(record, "startDate")
            ? displayDate({ date: get(record, "startDate") })
            : "-",
        key: "dueDate",
      },
      {
        title: LIST.CONTACT,
        dataIndex: "project",
        key: "contact",
        //   sorter: true,
        render: (record) => (
          <div className="task-list__contact-cell">
            <Avatar className="task-list__avatar">
              {upperCase(
                `${
                  get(record, "contact.user.firstname")?.substring(0, 1) || ""
                } ${
                  get(record, "contact.user.lastname")?.substring(0, 1) || ""
                }`,
              )}
            </Avatar>
            <div className="task-list__contact-info">
              <div className="task-list__contact-name">{` ${get(
                record,
                "contact.user.firstname",
              )} ${get(record, "contact.user.lastname")}`}</div>
              <div className="task-list__contact-dob">
                {displayDate({
                  date: get(record, "contact.user.birthDate"),
                  format: DATE_FORMAT,
                })}
              </div>
            </div>
          </div>
        ),
      },
      {
        title: LIST.COORDINATES,
        dataIndex: "project",
        //   sorter: true,
        render: (record) => {
          const email =
            get(record, "contact.telecoms", [])?.find(
              ({ system }) => system === CONTACT_POINT_SYSTEM.EMAIL,
            )?.value || "-";
          const phone =
            get(record, "contact.telecoms", [])?.find(
              ({ system }) => system === CONTACT_POINT_SYSTEM.PHONE,
            )?.value || "-";
          return (
            <div className="task-list__coordinates-cell">
              <div className="task-list__coordinate">
                <MdOutlinePhone className="task-list__coordinate-icon" />
                <span>{phone}</span>
              </div>
              <div className="task-list__coordinate">
                <MdOutlineMail className="task-list__coordinate-icon" />
                <span>{email}</span>
              </div>
            </div>
          );
        },
      },
      {
        title: LIST.STATUS,
        dataIndex: "status",
        render: (record) => (
          <div
            className={classNames("task-list__tag", {
              "task-list__tag--success": record === TASK_STATUSES.COMPLETED,
            })}
          >
            {TASK_STATUS_ICON[record]} {LIST.STATUSES[record] || record}
          </div>
        ),
        key: "status",
      },
      {
        title: LIST.ASSIGNED_TO,
        dataIndex: "project",
        render: (record) => (
          <>
            {get(record, "author.firstname")} {get(record, "author.lastname")}
          </>
        ),
        key: "author",
      },
      {
        title: LIST.ACTIONS,
        dataIndex: "id",
        align: "right",
        render: (id, task) => {
          const startDate = get(task, "event.startDate");
          const isOverdue =
            startDate &&
            dayjs(startDate).isBefore(dayjs()) &&
            task.status !== TASK_STATUSES.COMPLETED;
          const isFutureReminder =
            startDate && dayjs(startDate).isAfter(dayjs());
          const reminderCancelled = task.reminderCancelled === true;
          const hasUnreadReminderNotification = unreadReminderTaskIds.has(id);
          return (
            <div className="d-flex gap-4 justify--end pointer">
              {isOverdue && hasUnreadReminderNotification && (
                <Tooltip title={LIST.DISMISS_NOTIFICATION}>
                  <MdNotificationsOff
                    color={COLORS.C_WARNING}
                    size={24}
                    onClick={() => onDismissNotification(id)}
                  />
                </Tooltip>
              )}
              {isFutureReminder && !reminderCancelled && (
                <Tooltip title={LIST.CANCEL_REMINDER}>
                  <MdSchedule
                    color={COLORS.C_PRIMARY}
                    size={24}
                    onClick={() => onCancelReminder(id)}
                  />
                </Tooltip>
              )}
              {isFutureReminder && reminderCancelled && (
                <Tooltip title={LIST.REACTIVATE_REMINDER}>
                  <MdNotifications
                    color={COLORS.C_PRIMARY}
                    size={24}
                    onClick={() => onReactivateReminder(id)}
                  />
                </Tooltip>
              )}
              <MdDeleteOutline
                color={COLORS.C_DANGER}
                size={24}
                onClick={() => onDelete(id)}
              />
              <MdOpenInNew
                color={COLORS.C_PRIMARY}
                size={24}
                onClick={() => onOpenDetail(task)}
              />
            </div>
          );
        },
      },
    ],
    [
      LIST,
      onDelete,
      onOpenDetail,
      onDismissNotification,
      onCancelReminder,
      onReactivateReminder,
      unreadReminderTaskIds,
    ],
  );

  const TasksView = useCallback(
    ({ count, data, setValues, skip }) => (
      <>
        <Table
          dataSource={data}
          columns={columns}
          className="task-list__table"
          scroll={{ x: true }}
          rowKey={"id"}
          pagination={false}
          locale={{
            emptyText: resolveTaskEmptyMessage({
              hasSearch: Boolean(String(searchValue || "").trim()),
              searchEmptyMessage: LIST.SEARCH_EMPTY,
              listEmptyMessage: LIST.EMPTY,
            }),
          }}
        />
        <Pagination count={count} setValues={setValues} skip={skip || 0} />
      </>
    ),
    [columns, searchValue, LIST.SEARCH_EMPTY, LIST.EMPTY],
  );

  const memoFilter = useMemo(
    () => ({ ...filter, ...pagination }),
    [filter, pagination],
  );

  return (
    <>
      <div className="task-list">
        <Header
          key={"header"}
          title={LIST.HEADER.TITLE}
          // actions={[
          //   {
          //     icon: <TbFileExport size={20} />,
          //     text: LIST.HEADER.CREATE,
          //     onClick: () => null,
          //   },
          // ]}
        />
        <div className="task-list__stats-section">
          <div className="task-list__stats-title">{LIST.STATS.TITLE}</div>
          <div className="task-list__stats">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="task-list__stat-card">
                  <div className="task-list__stat-value">{stat.value}</div>
                  <div
                    className={`task-list__stat task-list__stat--${stat.color}`}
                  >
                    <div className={`task-list__stat-icon`}>
                      <IconComponent />
                    </div>
                    <div> {stat.label} </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="m-bottom-24" />
        <div className="task-list__main">
          <div className="task-list__filters">
            <div className="task-list__filter-tabs-container">
              <div className="task-list__filter-tabs">
                <div
                  className={`task-list__filter-tab ${
                    activeTab === "all" ? "task-list__filter-tab--active" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("all");
                    onSearch({ accessor: "where.status", value: undefined });
                  }}
                >
                  {LIST.FILTERS_TABS.ALL}
                </div>
                <div
                  className={`task-list__filter-tab task-list__filter-tab--with-icon ${
                    activeTab === "todo" ? "task-list__filter-tab--active" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("todo");
                    onSearch({
                      accessor: "where.status",
                      value: TASK_STATUSES.IN_PROGRESS,
                    });
                  }}
                >
                  <MdOutlineFilterTiltShift />
                  {LIST.FILTERS_TABS.TODO}
                </div>
                <div
                  className={`task-list__filter-tab task-list__filter-tab--with-icon ${
                    activeTab === "done" ? "task-list__filter-tab--active" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("done");
                    onSearch({
                      accessor: "where.status",
                      value: TASK_STATUSES.COMPLETED,
                    });
                  }}
                >
                  <MdOutlineCheckCircle />
                  {LIST.FILTERS_TABS.DONE}
                </div>
              </div>
            </div>
            <div className="task-list__filter-right-container">
              <div className="task-list__date-range">
                <div className="task-list__date-trigger" onClick={onOpenRange}>
                  <MdOutlineCalendarToday />
                </div>
                <Input
                  type="date"
                  variant="range"
                  open={isRangeOpen}
                  onOpenChange={onRangeOpenChange}
                  className="task-list__date-picker"
                  onChange={(_, dates) => {
                    onDateSearch(dates);
                  }}
                />
              </div>
              <div className="task-list__search">
                <Input
                  placeholder={LIST.SEARCH_PLACEHOLDER}
                  value={searchValue}
                  onChange={({ target: { value } }) => setSearchValue(value)}
                  // allowClear
                />
                <Button
                  type="secondary"
                  icon={<MdOutlineSearch />}
                  className="task-list__search-btn"
                  onClick={() =>
                    onSearch(buildTaskTitleSearchFilter(searchValue))
                  }
                />
              </div>
            </div>
          </div>
          <Tasks
            View={TasksView}
            NoData={() => (
              <div className="empty__table">
                <p>
                  {resolveTaskEmptyMessage({
                    hasSearch: Boolean(String(searchValue || "").trim()),
                    searchEmptyMessage: LIST.SEARCH_EMPTY,
                    listEmptyMessage: LIST.EMPTY,
                  })}
                </p>
              </div>
            )}
            setValues={setPagination}
            skip={pagination.skip}
            filter={memoFilter}
          />
        </div>
      </div>
    </>
  );
};

export default List;
